import React, { useState, useRef } from 'react';
import { Upload, X, FileText, Image as ImageIcon, Loader2 } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';

interface S3UploadProps {
    encounterId: string;
    onUploadComplete: (files: any[]) => void;
    className?: string;
}

export const S3Upload: React.FC<S3UploadProps> = ({ encounterId, onUploadComplete, className }) => {
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const processFile = async (file: File) => {
        setIsUploading(true);
        setError(null);

        try {
            // 1. Initiate upload
            const initResponse = await fetch(API_ENDPOINTS.ENCOUNTER_ATTACHMENTS.INITIATE_UPLOAD, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    encounter_id: encounterId,
                    filename: file.name,
                    content_type: file.type,
                    size_bytes: file.size
                })
            });

            if (!initResponse.ok) throw new Error('Failed to initiate upload');

            const { upload_url, fields, s3_key, file_id } = await initResponse.json();

            // 2. Upload to S3
            const formData = new FormData();
            Object.entries(fields).forEach(([key, value]) => {
                formData.append(key, value as string);
            });
            formData.append('file', file);

            const uploadResponse = await fetch(upload_url, {
                method: 'POST',
                body: formData
            });

            if (!uploadResponse.ok) throw new Error('Failed to upload to S3');

            // 3. Complete upload
            const completeResponse = await fetch(`${API_ENDPOINTS.ENCOUNTER_ATTACHMENTS.COMPLETE_UPLOAD}?encounter_id=${encounterId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    s3_key,
                    filename: file.name,
                    content_type: file.type,
                    size_bytes: file.size,
                    file_id
                })
            });

            if (!completeResponse.ok) throw new Error('Failed to complete upload');

            const updatedFiles = await completeResponse.json();
            onUploadComplete(updatedFiles);

        } catch (error) {
            console.error('Upload error:', error);
            alert('Failed to upload file. Please try again.');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) processFile(file);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) processFile(file);
    };

    return (
        <div className={className}>
            <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !isUploading && fileInputRef.current?.click()}
                className={`
                    relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                    ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
                    ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
                `}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="hidden"
                    accept="image/*,application/pdf"
                />

                <div className="flex flex-col items-center justify-center gap-2">
                    {isUploading ? (
                        <>
                            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                            <p className="text-sm font-medium text-gray-900">Uploading...</p>
                        </>
                    ) : (
                        <>
                            <div className="p-2 bg-gray-100 rounded-full">
                                <Upload className="w-6 h-6 text-gray-600" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-900">
                                    Click to upload or drag and drop
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                    PDF, PNG, JPG up to 10MB
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
